// Client-side image format conversion via canvas. Decode any browser-supported
// image, optionally downscale, and re-encode to PNG/JPEG/WebP. Fully local.

export type OutputFormat = 'png' | 'jpeg' | 'webp';

export interface ConvertOptions {
  format: OutputFormat;
  /** 0..1, used for jpeg/webp only. */
  quality: number;
  /** Optional max width in px; 0 = keep original. Height scales proportionally. */
  maxWidth: number;
}

export interface ConvertResult {
  blob: Blob;
  url: string;
  width: number;
  height: number;
  bytes: number;
}

const MIME: Record<OutputFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

export const EXT: Record<OutputFormat, string> = { png: 'png', jpeg: 'jpg', webp: 'webp' };

export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    img.src = url;
  });
}

export async function convertImage(img: HTMLImageElement, opts: ConvertOptions): Promise<ConvertResult> {
  const scale = opts.maxWidth > 0 && img.width > opts.maxWidth ? opts.maxWidth / img.width : 1;
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // JPEG has no alpha — fill white so transparent areas are not black.
  if (opts.format === 'jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Encoding failed'))),
      MIME[opts.format],
      opts.format === 'png' ? undefined : opts.quality,
    );
  });

  return { blob, url: URL.createObjectURL(blob), width, height, bytes: blob.size };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
